/** @format */
import { omit } from 'lodash';

import { productConditionStringToEnum } from '@toucan/client/global/product';
import {
    Address,
    CustomerDetails,
    DeliverySummary,
    DeliveryType,
    DisputeItem,
    LineItemState,
    ProductItemSummary,
    PublicTransactionStage,
    TransactionKind,
    TransactionState,
    TransactionSummary,
    TransactionSummaryAddress,
} from '@toucan/client/global/transaction';

type TransformTransactionSummaryOptions = {
    itemsToSellStatesToExclude?: LineItemState[];
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    allowUnknownCondition?: boolean;
};

type ItemSellingTransform = {
    itemsToSell: ProductItemSummary[];
    itemsToReturn: ProductItemSummary[];
};

export const transformCustomer = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
    customer: any
): { customerDetails: CustomerDetails } => {
    if (!customer) {
        // This is to replicate the pre-existing behaviour
        // without getting upset about types
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Legacy code: do not use as an example.
        return { customerDetails: customer };
    }

    return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Legacy code: do not use as an example.
        customerDetails: {
            ...customer,
            // This probably ought to throw if the api data is malformed.
            // Optional chain to preserve legacy behaviour until we have more
            // robust fixtures/testing in place for apis.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
            emailAddress: customer?.emailAddress?.trim(),
        },
    };
};
export const transactionTemplate: TransactionSummary = {
    kind: TransactionKind.TRANSACTION_SUMMARY,
    id: '',
    market: '',
    reference: '',
    customerDetails: {
        firstName: '',
        lastName: '',
        emailAddress: '',
        phoneNumber: '',
        receiveOffersViaEmail: false,
        receiveOffersViaText: false,
    },
    customerId: -1,
    billingAddress: {
        id: 0,
        addressee: '',
        line1: '',
        line2: '',
        town: '',
        county: '',
        state: '',
        postcode: '',
        zipcode: '',
        country: '',
        deliveryDefault: false,
        billingDefault: false,
        nickname: '',
    },
    state: TransactionState.UNDEFINED,
    stage: PublicTransactionStage.UNDEFINED_STAGE,
    disputes: [],
    itemsToSell: [],
    itemsToBuy: [],
    itemsToReturn: [],
    collections: [],
    shipments: [],
    deliveries: [],
    totals: {
        totalIncTax: 0,
        totalExTax: 0,
        totalTax: 0,
        totalPayments: 0,
        balance: 0,
    },
    promotions: [],
    createdDate: '',
    modifiedDate: '',
    tax: [],
    isWithinRma: false,
};

/**
 * Sort the deliveries returned from the transaction API into
 * two buckets; collections and shipments.
 *
 * @param transaction
 */
function transformDeliveries(
    transaction: TransactionSummary
): TransactionSummary {
    const { deliveries, ...newTransaction } = transaction;

    if (!deliveries) {
        return {
            ...transaction,
            collections: [],
            shipments: [],
        };
    }

    // Match delivery type
    function deliveryTypeEquals(deliveryType: DeliveryType) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
        return function (delivery: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
            return delivery.deliveryType === deliveryType;
        };
    }

    // Ensure that a delivery option is in the correct shape
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
    function transformDeliveryData(delivery: any): DeliverySummary {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Legacy code: do not use as an example.
        const transformedData = {
            ...delivery,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
            deliveryOption: parseInt(delivery.deliveryOption),
        };

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
        if (delivery.address) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
            transformedData.address = transformAddress(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
                delivery.address as TransactionSummaryAddress
            );
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Legacy code: do not use as an example.
        return transformedData;
    }

    const collections = deliveries
        .filter(deliveryTypeEquals(DeliveryType.COLLECTION))
        .map(transformDeliveryData);

    const shipments = deliveries
        .filter(deliveryTypeEquals(DeliveryType.SHIPMENT))
        .map(transformDeliveryData);

    return {
        ...newTransaction,
        deliveries,
        collections,
        shipments,
    };
}

/**
 * Transforms a transaction summary address into a regular Address
 * by copying over the customerAddressId in place of the ID stored on the
 * transaction and ensuring that zipcode/state are used for US addresses.
 *
 * @param address
 * @return Address
 */
function transformAddress(address: TransactionSummaryAddress): Address {
    const updatedAddress = {
        ...omit(address, 'customerAddressId'),
        id: address.customerAddressId,
    };

    if (address.country === 'US') {
        updatedAddress.state = address.county;
        updatedAddress.zipcode = address.postcode;
        delete updatedAddress.county;
        delete updatedAddress.postcode;
    }

    return updatedAddress;
}

/**
 * Partition items to sell and items to return based on the item state
 * @param items
 */
function partitionItemsSelling(
    items: ProductItemSummary[]
): ItemSellingTransform {
    return items.reduce(
        (accumulator: ItemSellingTransform, item: ProductItemSummary) => {
            const key =
                item.state === LineItemState.RETURNING
                    ? 'itemsToReturn'
                    : 'itemsToSell';
            accumulator[key].push(item);
            return accumulator;
        },
        { itemsToSell: [], itemsToReturn: [] }
    );
}

export function transformTransactionSummary(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
    data: any,
    {
        itemsToSellStatesToExclude = [
            LineItemState.CANCELLED,
            LineItemState.DECLINED,
        ],
        allowUnknownCondition = false,
    }: TransformTransactionSummaryOptions = {}
): TransactionSummary {
    const transactionSummary: TransactionSummary = {
        ...transactionTemplate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example.
        ...transformDeliveries(data),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
        ...partitionItemsSelling(data.itemsToSell),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
        ...transformCustomer(data.customer),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
        customerId: data.customer?.customerId ?? transactionTemplate.customerId,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
        isWithinRma: data.isWithinRma ?? transactionTemplate.isWithinRma,
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
    if (data.billingAddress) {
        transactionSummary.billingAddress = transformAddress(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
            data.billingAddress as TransactionSummaryAddress
        );
    }

    // Updates the ProductItemSummary response from the api to use an enum for the condition
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
    const updateItemConditionsToEnum = (item: any) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Legacy code: do not use as an example.
        return {
            ...item,
            condition: productConditionStringToEnum(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
                item.condition,
                allowUnknownCondition
            ),
        };
    };

    // eslint-disable-next-line @mpb-com/mpb/boolean-function-prefix -- Legacy code: do not use as an example.
    const removeCancelledItems = (item: ProductItemSummary | DisputeItem) => {
        return item.state !== LineItemState.CANCELLED;
    };

    // eslint-disable-next-line @mpb-com/mpb/boolean-function-prefix -- Legacy code: do not use as an example.
    const filterSellItems = (item: ProductItemSummary) => {
        return !itemsToSellStatesToExclude.includes(item.state);
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Legacy code: do not use as an example.
    transactionSummary.itemsToBuy = transactionSummary.itemsToBuy
        .filter(removeCancelledItems)
        .map(updateItemConditionsToEnum);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Legacy code: do not use as an example.
    transactionSummary.itemsToSell = transactionSummary.itemsToSell
        .filter(filterSellItems)
        .map(updateItemConditionsToEnum);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Legacy code: do not use as an example.
    transactionSummary.itemsToReturn = transactionSummary.itemsToReturn.map(
        updateItemConditionsToEnum
    );

    transactionSummary.disputes =
        transactionSummary.disputes.filter(removeCancelledItems);

    if (transactionSummary.initialQuote) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Legacy code: do not use as an example.
        transactionSummary.initialQuote.items =
            transactionSummary.initialQuote.items.map(
                updateItemConditionsToEnum
            );
    }

    if (transactionSummary.quote) {
        transactionSummary.quote.items = transactionSummary.quote.items.map(
            (item) => {
                const lineItems = [
                    ...transactionSummary.itemsToSell,
                    ...transactionSummary.itemsToReturn,
                ];
                const lineItem = lineItems.find(
                    (lineItem) => lineItem.id === item.lineItemId
                );

                return {
                    ...item,
                    lineItemState:
                        lineItem?.state ?? LineItemState.UNDEFINED_STATE,
                    condition: productConditionStringToEnum(
                        item.condition,
                        allowUnknownCondition
                    ),
                };
            }
        );
    }

    return transactionSummary;
}
